import React from 'react';
import {Space} from '@mc/ui-v4';
import {Zoomer} from './content/Zoomer';
import {Search} from './content/Search';
import {GeolocationButton} from './content/GeolocationButton';
import {LayerId} from '@mc/react-mapio';
import './Overhead.scss';
import {LayerSelector} from './content/LayerSelector';
import {TopicVO} from '../../api';
import {useAppConfig} from '@mc/react-app-base';
import {useIsMobile} from '../../hooks/useIsMobile';
import {useMapioContext} from '../../mapioContext';
import {useLayersConfig} from '../../hooks/useLayersConfig';
import {WaitForQueryData} from '@mc/react-app-v4';
import {UndoButton} from '../toolbar/UndoButton';
import {GeometryUndoStep} from '../toolbar/undoTypes';

type Props = {
    backgroundLayer: LayerId;
    setBackgroundLayer: React.Dispatch<React.SetStateAction<LayerId>>;
    foregroundLayer: LayerId;
    setForegroundLayer: React.Dispatch<React.SetStateAction<LayerId>>;
    /** Historie geometrie pro ovládání "Zpět/Vpřed" (jen editační režim). */
    undoSteps: GeometryUndoStep[];
    redoCount: number;
    onUndoStep: (index: number) => void;
    onRedo: () => void;
};

export const Overhead: React.FC<Props> = ({
    backgroundLayer,
    setBackgroundLayer,
    foregroundLayer,
    setForegroundLayer,
    undoSteps,
    redoCount,
    onUndoStep,
    onRedo,
}) => {
    const layersQuery = useLayersConfig();
    const isMobile = useIsMobile();

    // @ts-ignore
    const {serverPublic} = useAppConfig();

    const mapioContext = useMapioContext();
    const {mapRef, disabled, topic} = mapioContext.value;

    return (
        <WaitForQueryData queries={[layersQuery]}>
            {() => {
                const layersData = layersQuery.data;
                const bgLayers = Object.values(layersData)?.filter(layer => topic?.backgroundLayers?.includes(layer.id)) ?? [];
                const fgLayers = Object.values(layersData)?.filter(layer => topic?.foregroundLayers?.includes(layer.id)) ?? [];
                // Mobilní zjednodušené veřejné Mapio: ovládání mapy nahoře – vlevo LayerSelector + geolokace, vpravo zoom.
                const mobilePublicSimple = isMobile && serverPublic && !!topic?.publicSimpleView;

                const showSearch = topic?.showSearch && (!serverPublic || !topic?.publicSimpleView);

                const layerSelector = (topic?.showBackgroundSelection || topic?.showForegroundSelection) && (
                    <LayerSelector
                        disabled={disabled}
                        serverPublic={serverPublic}
                        topic={topic}
                        backgroundLayerId={topic?.showBackgroundSelection ? backgroundLayer : undefined}
                        foregroundLayerId={topic?.showForegroundSelection ? foregroundLayer : undefined}
                        backgroundLayers={topic?.showBackgroundSelection ? bgLayers : undefined}
                        foregroundLayers={topic?.showForegroundSelection ? fgLayers : undefined}
                        onSetLayer={(t, id) => {
                            if (t == 0) {
                                mapRef.current?.setBackgroundLayer(id);
                                setBackgroundLayer(id);
                            } else {
                                mapRef.current?.setForegroundLayer(id);
                                setForegroundLayer(id);
                            }
                        }}
                    />
                );

                if (mobilePublicSimple) {
                    return (
                        <Space className={'position-absolute overhead-wrapper public mobile'}>
                            <div className={'overhead-left'}>
                                {layerSelector}
                                <GeolocationButton mapRef={mapRef} disabled={disabled} round />
                            </div>
                            {topic?.showZoomButtons && <Zoomer mapRef={mapRef} disabled={disabled} round />}
                        </Space>
                    );
                }

                return (
                    <Space className={`position-absolute overhead-wrapper ${serverPublic ? 'public' : ''} ${isMobile ? 'mobile' : ''}`}>
                        {topic?.showZoomButtons && !isMobile && <Zoomer mapRef={mapRef} disabled={disabled} />}
                        {/* Desktop: skok na aktuální polohu zařízení (MCW-28991) je součástí řady ovládání mapy vedle zoomu. */}
                        {!isMobile && <GeolocationButton mapRef={mapRef} disabled={disabled} />}
                        {!serverPublic && <UndoButton steps={undoSteps} redoCount={redoCount} onUndoStep={onUndoStep} onRedo={onRedo} disabled={disabled} />}
                        {layerSelector && <div className={`${isMobile && serverPublic ? 'order-2' : ''}`}>{layerSelector}</div>}
                        {isMobile ? (
                            // Mobil: geolokace pod vyhledáváním, ve stejném sloupci a se stejným vzhledem.
                            <div className={`overhead-search-column ${serverPublic ? 'order-1' : ''}`}>
                                {showSearch && <Search />}
                                <GeolocationButton mapRef={mapRef} disabled={disabled} />
                            </div>
                        ) : (
                            showSearch && (
                                <div>
                                    <Search />
                                </div>
                            )
                        )}
                    </Space>
                );
            }}
        </WaitForQueryData>
    );
};
